import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon, PhotoIcon, CubeIcon, TagIcon, BuildingOfficeIcon,
  Cog6ToothIcon, EnvelopeIcon, ArrowRightOnRectangleIcon,
  Bars3Icon, XMarkIcon, GlobeAltIcon, SparklesIcon, BriefcaseIcon, MegaphoneIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { ADMIN_BASE, PUBLIC_SITE_URL } from '../utils/adminBase';

const NAV = [
  { to: ADMIN_BASE || '/', label: 'Dashboard', icon: HomeIcon, end: true },
  { to: `${ADMIN_BASE}/hero`, label: 'Hero Slides', icon: PhotoIcon },
  { to: `${ADMIN_BASE}/categories`, label: 'Categories', icon: TagIcon },
  { to: `${ADMIN_BASE}/products`, label: 'Products', icon: CubeIcon },
  { to: `${ADMIN_BASE}/brands`, label: 'Brands', icon: SparklesIcon },
  { to: `${ADMIN_BASE}/clients`, label: 'Clients', icon: BuildingOfficeIcon },
  { to: `${ADMIN_BASE}/careers`, label: 'Careers', icon: BriefcaseIcon },
  { to: `${ADMIN_BASE}/promotions`, label: 'Promotional', icon: MegaphoneIcon },
  { to: `${ADMIN_BASE}/messages`, label: 'Messages', icon: EnvelopeIcon },
  { to: `${ADMIN_BASE}/settings`, label: 'Site Settings', icon: Cog6ToothIcon },
  { to: `${ADMIN_BASE}/help`, label: 'Help & Guide', icon: QuestionMarkCircleIcon },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const nav = NAV.filter(item => !item.adminOnly || admin?.role === 'admin');

  const handleLogout = () => { logout(); navigate(`${ADMIN_BASE}/login`); };

  const Sidebar = () => (
    <aside className="flex flex-col h-full bg-primary-dark text-white w-64 shrink-0 print:hidden">
      {/* Brand */}
      <div className="px-6 py-5 border-b border-white/10">
        <div className="font-display font-bold text-lg tracking-widest text-accent">SANKYO</div>
        <div className="text-xs text-gray-400 mt-0.5">Admin Dashboard</div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm rounded transition-all duration-150 ${isActive
                ? 'bg-accent text-white font-medium'
                : 'text-gray-300 hover:bg-white/10 hover:text-white'}`
            }>
            <Icon className="w-4 h-4 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-white/10 space-y-1">
        <a href={PUBLIC_SITE_URL} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded">
          <GlobeAltIcon className="w-4 h-4" /> View Website
        </a>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-400 hover:text-red-400 transition-colors rounded">
          <ArrowRightOnRectangleIcon className="w-4 h-4" /> Logout
        </button>
        <div className="px-4 pt-2 text-xs text-gray-500">Logged in as <span className="text-accent">{admin?.username}</span></div>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden print:h-auto print:overflow-visible">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex h-full">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="flex h-full"><Sidebar /></div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden print:overflow-visible">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 flex items-center justify-between print:hidden">
          <button className="lg:hidden p-1.5 text-gray-500 hover:text-primary" onClick={() => setSidebarOpen(true)}>
            <Bars3Icon className="w-5 h-5" />
          </button>
          <div className="text-sm text-gray-500">Welcome back, <span className="font-medium text-primary">{admin?.username}</span></div>
          <button onClick={handleLogout} className="text-xs text-gray-500 hover:text-red-500 transition-colors flex items-center gap-1">
            <ArrowRightOnRectangleIcon className="w-4 h-4" /> Logout
          </button>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 print:overflow-visible print:h-auto print:p-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
